import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { rvApi } from '../api';
import { rulesViolationReportItem } from '../../../constants/routes';

function RulesViolationReports() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfBusyId, setPdfBusyId] = useState(null);

  const downloadReportPdf = async (itemId, violationNumber) => {
    setPdfBusyId(itemId);
    setError('');
    try {
      const res = await rvApi.reportItemPdf(itemId);
      const ctype = (res.headers['content-type'] || '').toLowerCase();
      if (ctype.includes('application/json')) {
        const text = await res.data.text();
        let msg = 'PDF request failed';
        try {
          msg = JSON.parse(text).error || msg;
        } catch {
          msg = text || msg;
        }
        throw new Error(msg);
      }
      const blob =
        res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rules-violation-${violationNumber || itemId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = e.message || 'PDF download failed';
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          msg = JSON.parse(t).error || msg;
        } catch {
          /* keep msg */
        }
      } else if (e.response?.data?.error) {
        msg = e.response.data.error;
      }
      setError(msg);
    } finally {
      setPdfBusyId(null);
    }
  };

  useEffect(() => {
    rvApi.properties().then((res) => setProperties(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!propertyId) {
      setLocations([]);
      setLocationId('');
      return;
    }
    rvApi.locations(propertyId).then((res) => setLocations(res.data)).catch(() => setLocations([]));
  }, [propertyId]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await rvApi.reports({
          propertyId: propertyId || undefined,
          locationId: locationId || undefined,
        });
        if (!cancel) setRows(res.data);
      } catch (e) {
        if (!cancel) setError(e.response?.data?.error || e.message || 'Failed to load reports');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [propertyId, locationId]);

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <div className="app-shell-page">
      <div className="app-page-header mb-4">
        <h2 className="h5 mb-1">Reports</h2>
        <p className="text-muted small mb-0">Filter violation reports by property and site.</p>
      </div>
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card app-card mb-4">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label small mb-0">Property</label>
              <select
                className="form-select form-select-sm"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
              >
                <option value="">All</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small mb-0">Site / location</label>
              <select
                className="form-select form-select-sm"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={!propertyId}
              >
                <option value="">All</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="app-loading text-muted" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          <span>Loading…</span>
        </div>
      ) : (
        <motion.div
          key={`rv-rep-${propertyId}-${locationId}`}
          initial={reduceMotion ? false : { opacity: 0.9, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="card app-card">
            <div className="card-body p-0">
              <div className="app-table-wrap table-responsive">
                <table className="table table-striped table-hover table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Reported</th>
                      <th>Property</th>
                      <th>Site</th>
                      <th>Group</th>
                      <th>Violation</th>
                      <th>#</th>
                      <th>Due</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-4 small app-empty-hint">
                          No violations found
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr
                          key={r.item_id}
                          role="button"
                          tabIndex={0}
                          className="rv-report-row"
                          onClick={() => navigate(rulesViolationReportItem(r.item_id))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(rulesViolationReportItem(r.item_id));
                            }
                          }}
                        >
                          <td>{fmt(r.inspected_at)}</td>
                          <td>{r.property_name}</td>
                          <td>{r.location_name || '—'}</td>
                          <td>{r.group_name}</td>
                          <td>{r.violation_type_name}</td>
                          <td>{r.violation_number}</td>
                          <td>{r.due_date_correction || '—'}</td>
                          <td>
                            <div
                              className="d-flex flex-wrap gap-1 justify-content-end"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              role="presentation"
                            >
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-dark"
                                disabled={pdfBusyId != null}
                                title="Download PDF: violation details, notes, and pictures"
                                onClick={() => downloadReportPdf(r.item_id, r.violation_number)}
                              >
                                {pdfBusyId === r.item_id ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" aria-hidden />
                                    PDF…
                                  </>
                                ) : (
                                  'PDF'
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                disabled={pdfBusyId != null}
                                onClick={() => navigate(rulesViolationReportItem(r.item_id))}
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default RulesViolationReports;
